import axios, { type AxiosResponse, type CancelTokenSource } from 'axios';
import get from 'lodash/get';

import { v5CountsUrl, v5PersonalisedCountsUrl } from 'src/config';
import {
  createAuthenticatedHttpClient,
  createUnauthenticatedHttpClient,
  withDevAuth,
} from 'src/modules/seek-api-request';
import type { CountsApiResult } from 'src/store/search/types';
import type { CookieType } from 'src/types/cookie';

import { buildApiHeaders } from './utils/buildApiHeaders';
import { buildApiQuery } from './utils/buildApiQuery';
import { SEARCH_API_TIMEOUT } from './utils/constants';
import { getAuthClientDetails } from './utils/getAuthClientDetails';
import { getJobSearchDarkProdUrl } from './utils/getJobSearchDarkProdUrl';
import type { BaseAPIProps } from './utils/types';

let onlyCountsPromise: Promise<CountsApiResult> | undefined,
  onlyCountsPromiseSource: CancelTokenSource | undefined;

const getCountsUrl = ({
  isAuthenticated = false,
  cookies,
}: {
  isAuthenticated?: boolean;
  cookies: CookieType;
}) => {
  const darkProdSearchUrlCounts = getJobSearchDarkProdUrl({
    cookies,
    path: '/counts',
  });

  if (darkProdSearchUrlCounts) {
    return darkProdSearchUrlCounts;
  }

  if (isAuthenticated) {
    return v5PersonalisedCountsUrl;
  }
  return v5CountsUrl;
};

export const counts = ({
  searchParams,
  country,
  zone,
  sessionId,
  userClientId,
  cookies,
  requestId,
  timeout = SEARCH_API_TIMEOUT,
  testHeaders,
  locale,
  isAuthenticated,
  solId,
}: BaseAPIProps) => {
  if (onlyCountsPromise && onlyCountsPromiseSource) {
    onlyCountsPromiseSource.cancel();
  }

  const { isAuthenticatedClient, useDevAuth } =
    getAuthClientDetails(isAuthenticated);

  const config = {
    omitXSeekSiteHeader: true,
    defaultRequestConfig: {
      headers: {
        ...testHeaders,
        ...(useDevAuth ? withDevAuth(cookies.AUTH_TOKEN) : {}),
      },
    },
  };

  const httpClient = isAuthenticatedClient
    ? createAuthenticatedHttpClient(config)
    : createUnauthenticatedHttpClient(config);

  const countsPromise = httpClient
    .send({
      url: getCountsUrl({ isAuthenticated, cookies }),
      timeout,
      cancelToken: get(onlyCountsPromiseSource, 'token'),
      label: 'jobsearch-api-counts',
      params: buildApiQuery({
        sessionId,
        userClientId,
        searchQuery: searchParams,
        zone,
        country,
        locale,
        solId,
      }),
      headers: buildApiHeaders(requestId),
    })
    .then((response: AxiosResponse<CountsApiResult>) => response.data);

  const isClient = ENV.CLIENT;
  if (isClient) {
    onlyCountsPromise = countsPromise;
    onlyCountsPromiseSource = axios.CancelToken.source();
  }

  return countsPromise;
};
